import { useEffect, useMemo, useState } from "react";

import { useForm } from "react-hook-form";

import { TrashIcon } from "@heroicons/react/24/outline";
import { useQueryClient } from "@tanstack/react-query";

import useEbayAspects from "../../data/useEbayAspects";
import usePartAspects from "../../data/usePartAspects";
import useSinglePartMutations from "../../data/useSinglePartMutations";
import useDefaultCRUDHandlers from "../../hooks/useDefaultCRUDHandlers";
import { IEbayAspect, IPartAspect } from "../../models/EbayOffer";
import { IPart } from "../../models/Part";
import toTitleCase from "../../utils/toTitleCase";
import useNotification from "../notifications/useNotifications";
import { ButtonInput } from "../shared/Inputs/ButtonInput";
import ComboBoxInput from "../shared/Inputs/ComboBoxInput";
import ComboBoxMultipleInput from "../shared/Inputs/ComboBoxMultipleInput";
import LoadingWheel from "../shared/LoadingWheel";
import usePartDefaultCategories from "../../data/usePartDefaultCategories";
import { TextInput } from "../shared/Inputs/TextInput";
import checkEbayDisabled, { ebayChecks } from "../../utils/checkEbayDisabled";

// Also known as Item Aspects (eBay website says "Item Specifics" and the eBay API calls them "Aspects")
export default function EditListingSpecifics({ part, setPage }: { part: IPart, setPage?: any }) {
  const { publish } = useSinglePartMutations(part.id!);
  const { partAspects, createUpdate, createCustomAspects, remove } = usePartAspects(part.id);
  
  const offerCategoryId = part.offers?.[0]?.categoryId;
  const localDefaultCategoryId = part.category?.partCategoryDefaults?.[0]?.ebayCategoryId;
  const shouldFetchDefaultCategory = !offerCategoryId && !localDefaultCategoryId;

  const { defaultPartCategory } = usePartDefaultCategories({
    part,
    enabled: shouldFetchDefaultCategory
  });

  const ebayCategoryId = useMemo(
    () =>
      (offerCategoryId ??
        localDefaultCategoryId ??
        defaultPartCategory.data?.data?.partCategoryId ??
        ""
      ).toString().trim(),
    [offerCategoryId, localDefaultCategoryId, defaultPartCategory.data?.data?.partCategoryId]
  );

  const { aspects: ebayAspects } = useEbayAspects(ebayCategoryId);
  const { saveHandlers, deleteHandlers } = useDefaultCRUDHandlers("Aspects");
  const queryClient = useQueryClient();
  const { register, handleSubmit, getValues, setValue } = useForm({});
  const { addNotification } = useNotification();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isPublish, setIsPublish] = useState<boolean>(false);
  const [customAspects, setCustomAspects] = useState<IPartAspect[]>([]);
  const [categoryAspects, setCategoryAspects] = useState<IPartAspect[]>([]);
  const [usedAspects, setUsedAspects] = useState<number | undefined>(undefined);
  const [refresh, setRefresh] = useState<boolean>(true);
  const [errorCheck, setErrorCheck] = useState<ebayChecks | null>(null);


  const publishListing = () => {
    // console.log("Publish listing");

    publish.mutate(part.offers[0].id!, {
      onSuccess: () => {
        addNotification({
          variant: "success",
          primaryText: `Publish Successsful`,
        });
      },
      onError: (error) => {
        addNotification({
          variant: "error",
          primaryText: `Publish Failed - ${error}`,
        });
      },
      onSettled: () => {
        setIsPublish(false);
      }
    });
  };

  const onSubmit = (data: any, publish: boolean): any => {
    // console.log("Submit:", publish);
    setIsPublish(publish);

    let currentAspects: any = {};

    //Create object of aspects with data
    Object.keys(data).forEach((key, i) => {
      if (data[key] === "" || key === "newAspectName" || key === "newAspectValue") {
        return;
      }
      currentAspects[key] = data[key]?.localizedValue ?? data[key]; //If it's a combo box, get the localized value
    });

    //Pull out required aspects
    const requiredAspects = ebayAspects.data?.filter(aspect => aspect.aspectConstraint.aspectRequired);
    let missingRequiredAspects: string[] = [];

    //Check that all required aspects have a value in body
    requiredAspects?.forEach(aspect => {
      if (!currentAspects[aspect.localizedAspectName]) {
        missingRequiredAspects.push(aspect.localizedAspectName);
      }
    });

    if (missingRequiredAspects.length > 0) {
      alert(`Please fill in the following required aspects: ${missingRequiredAspects.join(", ")}`);
      setIsPublish(false);
      return;
    }

    //Check for create or update
    let updateAspects: any = [];
    let createAspects: any = [];

    Object.keys(currentAspects).forEach((key: any) => {
      //Check if aspect is in partAspects
      if (partAspects.data?.data.find((partAspect: any) => partAspect.name === key)) {
        //Update
        let aspect = partAspects.data?.data.find((partAspect: any) => partAspect.name === key);
        updateAspects.push({
          ...aspect,
          name: key,
          value: currentAspects[key],
        });
      } else {
        //Create
        createAspects.push({
          name: key,
          value: currentAspects[key]
        });
      }
    });

    // console.log("Create:", createAspects);
    // console.log("Update:", updateAspects);

    const body = {
      partDataId: part.id,
      aspects: [...createAspects, ...updateAspects]
    };

    createUpdate.mutate(body, {
      onSuccess: () => {
        if (publish) {
          publishListing();
        } else {
          saveHandlers.onSuccess();
          part.aspects = [...body.aspects];
          handleErrorCheck();
        }
      },
      onError: (error) => {
        saveHandlers.onError();
        setIsPublish(false);
      }

    });
  };

  const addCustomAspect = (name: string, value: string) => {
    let createAspects: any = [];

    console.log("Custom Aspect:", name, value);

    if (!name) return;

    createAspects.push({
      name: name,
      value: value,
      isCustom: true
    });

    const body = {
      partDataId: part.id,
      aspects: [...createAspects]
    };

    createUpdate.mutate(body, {
      onSuccess: () => {
        setValue("newAspectName", "");
        setValue("newAspectValue", "");
        setValue(name, value);
        saveHandlers.onSuccess();
      },
      onError: (error) => {
        saveHandlers.onError();
      }

    });
  };

  const deleteCustomAspect = (aspect: IPartAspect) => {

    remove.mutate(aspect.id, {
      ...deleteHandlers,
      onSuccess: () => {
        //Else it gets stored in form data and created as not custom on save
        setValue(aspect.name, "");
        deleteHandlers.onSuccess();
      },
    });
  };

  const getAspectInitialValue = (aspect: IEbayAspect) => {
    let existingValue = partAspects.data?.data.find((partAspect: IPartAspect) => partAspect.name === aspect.localizedAspectName)?.value;

    if (existingValue != null) return existingValue;

    let lookFor = "";

    // Pre fill known values
    switch (aspect.localizedAspectName) {
      case "Brand":
        lookFor = part.vehicle?.combinedMake ?? "";
        break;
      case "Manufacturer Part Number":
        return part.partNumber ?? "";
      case "Placement on Vehicle":
        lookFor = part.titleSanitised ?? "";
        break;
      case "Item Height":
        return part.height ? `${part.height}cm` : "";
      case "Item Length":
        return part.length ? `${part.length}cm` : "";
      case "Item Width":
        return part.width ? `${part.width}cm` : "";
      case "Item Weight":
        return part.weight ? `${part.weight}kg` : "";
    }

    //Look for the value in the aspect values
    if (lookFor !== "") {
      //Free text aspects come back from eBay with no aspectValues at all
      const aspectValues = aspect.aspectValues ?? [];

      let idx = aspectValues.findIndex((value: any, i: number) => value.localizedValue?.toLowerCase() === lookFor.toLowerCase());

      if (idx === -1)
        idx = aspectValues.findIndex((value: any, i: number) => value.localizedValue?.toLowerCase().includes(lookFor.toLowerCase()));

      const val = aspectValues[idx];

      if (val == null)
        return "";

      return val.localizedValue;
    }
  }

  useEffect(() => {
    if (!ebayCategoryId) return;
    if (!ebayAspects.data && !ebayAspects.isFetching) {
      ebayAspects.refetch();
    }
  }, [ebayCategoryId, ebayAspects.data, ebayAspects.isFetching, ebayAspects.refetch]);

  useEffect(() => {
    if (
      (shouldFetchDefaultCategory && defaultPartCategory.isLoading) ||
      (ebayAspects.isLoading && !!ebayCategoryId) ||
      partAspects.isLoading
    ) {
      return;
    }

    if (isLoading) {

      if (!partAspects.data?.data || partAspects.data?.data.length <= 5) {
        return createCustomAspects.mutate(part.id, {
          onSuccess: () => {
            return;
          }
        });
      }

      //Set initial values
      partAspects.data?.data.forEach((partAspect: IPartAspect) => {
        setValue(partAspect.name, partAspect.value);
      });
    }

    let customAspects = partAspects.data?.data.filter((aspect: IPartAspect) => aspect.isCustom === true) ?? [];

    // Need to create copies of these to avoid mutating the original data
    let categoryAspects = part.category?.partCategoryDefaults?.[0]?.categoryCustomAspects ?? [];

    if (categoryAspects.length > 0) {
      //filter out category aspects we already have
      categoryAspects = categoryAspects.filter((aspect: IPartAspect) => {
        return !partAspects.data?.data.find((partAspect: IPartAspect) => partAspect.name === aspect.name);
      });

      if (categoryAspects.length > 0) {
        let createAspects: any[] = [];

        createAspects = categoryAspects.map((aspect: IPartAspect) => {
          return {
            ...aspect,
            id: undefined, // Ensure id is undefined for new aspects
            partCategoryDefaultsId: null,
            partDataId: part.id,
          };

        });

        const body = {
          partDataId: part.id,
          aspects: [...createAspects]
        };

        createUpdate.mutate(body, {
          onSuccess: (data) => {
            queryClient.invalidateQueries(["partAspects", part.id]);
          }
        });
      }
    }


    //Imported aspects are not custom but don't match the ebay Aspects
    let importedAspects = partAspects.data?.data.filter(
      (aspect: IPartAspect) => aspect.isCustom === false &&
        !ebayAspects.data?.find((ebayAspect: IEbayAspect) => ebayAspect.localizedAspectName === aspect.name)
    ) ?? [];

    let allCustomAspects = [...importedAspects, ...customAspects];

    //Filter out any aspects that are from eBay
    allCustomAspects = allCustomAspects.filter((aspect: IPartAspect) => {
      return !ebayAspects.data?.find((ebayAspect: IEbayAspect) => ebayAspect.localizedAspectName === aspect.name);
    });

    allCustomAspects = allCustomAspects.sort((a: IPartAspect, b: IPartAspect) => {
      // Sort by isRequired && isFromCategory first
      const aRequiredFromCat = a.isRequired && a.isFromCategory;
      const bRequiredFromCat = b.isRequired && b.isFromCategory;
      if (aRequiredFromCat !== bRequiredFromCat) {
        return bRequiredFromCat ? 1 : -1;
      }

      // Then by isFromCategory
      if (a.isFromCategory !== b.isFromCategory) {
        return b.isFromCategory ? 1 : -1;
      }

      // Then by isRequired
      if (a.isRequired !== b.isRequired) {
        return b.isRequired ? 1 : -1;
      }

      // Otherwise, keep original order
      return 0;
    });

    setCustomAspects(allCustomAspects);

    let currentValues = getValues();
    let usedAspectCount = 0;
    Object.keys(currentValues).forEach((key, i) => {
      if (currentValues[key] === "" || key === "newAspectName" || key === "newAspectValue") {
        return;
      }
      usedAspectCount++;
    });

    setUsedAspects(usedAspectCount);
    setIsLoading(false);

  }, [
    shouldFetchDefaultCategory,
    defaultPartCategory.isLoading,
    ebayCategoryId,
    ebayAspects.isLoading,
    ebayAspects.data,
    partAspects.isLoading,
    partAspects.data,
    refresh
  ]);

  useEffect(() => {
    if (!isLoading) {
      handleErrorCheck();
    }
  }, [isLoading, part, partAspects.data, ebayAspects.data]);


  useEffect(() => {
    if (!setPage) return;

    //Scroll to top on page load
    window.scrollTo(0, 0);
  }, []);

  const sortByRequired = (a: IEbayAspect, b: IEbayAspect) => {
    if (a.aspectConstraint.aspectRequired === b.aspectConstraint.aspectRequired) {
      return 0;
    }

    if (a.aspectConstraint.aspectRequired) {
      return -1;
    }

    return 1;
  }

  const handleErrorCheck = () => {
    // Always pass category defaults so threshold-based checks (e.g. AutoAcceptThreshold)
    // are considered in addition to the offer's own fields.
    var defaults = part.category?.partCategoryDefaults?.[0];
    const error = checkEbayDisabled({ part, offer: part.offers ? part.offers[0] : undefined, partCategoryDefault: defaults });

    setErrorCheck(error);
  }


  if (isLoading || isPublish) {
    return <LoadingWheel />;
  }

  return (
    <div key={part.id}>
      <h1 className="text-lg font-semibold">Category Specifics</h1>
      <h2 className="text-sm">Maximum number of Item Aspects eBay allows: 45 {usedAspects !== undefined ? ` (${usedAspects}/45)` : "(0/45)"}</h2>

      <form onSubmit={handleSubmit(() => onSubmit(getValues(), false))} onChange={() => setRefresh(!refresh)} className="mt-2 space-y-2">

        {/* Custom Aspects Section */}
        {customAspects.length > 0 && (
          <div className="flex flex-col gap-1">
            <h2 className="text-lg font-semibold mb-1">Custom Aspects</h2>
            <div className="border border-gray-200 rounded">
              {customAspects.map((aspect: IPartAspect, index: number) => {
                const isEven = index % 2 === 0;
                const baseBg = isEven ? 'bg-gray-100' : 'bg-gray-200';


                return (
                  <div key={aspect.id} className={`flex items-center border-b border-gray-100 last:border-b-0 ${baseBg} transition-colors`}>
                    <div className="w-1/3 px-3 py-2 text-sm font-medium text-gray-700 border-r border-gray-300">
                      {toTitleCase(aspect.name)} {aspect.isRequired && " *"}
                    </div>
                    <div className="flex-1 px-3 py-2">
                      {aspect.options && aspect.options.length > 0 ? (
                        <ComboBoxInput
                          size="sm"
                          options={aspect.options.split(",").map((option: string) => ({ localizedValue: option.trim(), id: option.trim() }))}
                          searchField="localizedValue"
                          register={register}
                          setValue={setValue}
                          registerName={aspect.name}
                          initialValue={aspect.value}
                        />
                      ) : (
                        <TextInput
                          size="sm"
                          label=""
                          register={register}
                          registerName={aspect.name}
                          maxLength={50}
                        />
                      )}


                    </div>
                    {!aspect.isFromCategory && (
                      <div className="px-3 py-2">
                        <TrashIcon
                          className="h-4 w-4 cursor-pointer hover:text-red-600"
                          onClick={() => deleteCustomAspect(aspect)}
                        />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* eBay Aspects Section */}
        <div className="flex flex-col gap-1">
          <h2 className="text-lg font-semibold mb-1">eBay Aspects</h2>
          <div className="border border-gray-200 rounded">
            {[...(ebayAspects.data ?? [])].sort((a: IEbayAspect, b: IEbayAspect) => sortByRequired(a, b)).map((aspect: IEbayAspect, index: number) => {
              const isRequired = aspect.aspectConstraint.aspectRequired;
              const label = `${aspect.localizedAspectName}${isRequired ? " *" : ""}`;
              const initialValue = getAspectInitialValue(aspect);
              const isEven = index % 2 === 0;
              const baseBg = isEven ? 'bg-gray-100' : 'bg-gray-200';

              if (!aspect.aspectValues || aspect.aspectValues.length === 0) {
                return (
                  <div key={aspect.localizedAspectName} className={`flex items-center border-b border-gray-100 last:border-b-0 ${baseBg} transition-colors`}>
                    <div className="w-1/3 px-3 py-2 text-sm font-medium text-gray-700 border-r border-gray-300">
                      {toTitleCase(label)}
                    </div>
                    <div className="flex-1 px-3 py-2">
                      <TextInput
                        size="sm"
                        label=""
                        register={register}
                        registerName={aspect.localizedAspectName}
                        maxLength={50}
                        initialValue={initialValue}
                      />
                    </div>
                  </div>
                );
              }

              if (aspect.aspectConstraint.itemToAspectCardinality === "MULTI") {
                return (
                  <div key={aspect.localizedAspectName} className={`flex items-center border-b border-gray-100 last:border-b-0 ${baseBg} transition-colors`}>
                    <div className="w-1/3 px-3 py-2 text-sm font-medium text-gray-700 border-r border-gray-300">
                      {toTitleCase(label)} (Multi)
                    </div>
                    <div className="flex-1 px-3 py-2">
                      <ComboBoxMultipleInput
                        size="sm"
                        options={aspect.aspectValues}
                        searchField={"localizedValue"}
                        register={register}
                        setValue={setValue}
                        registerName={aspect.localizedAspectName}
                        initialValue={initialValue}
                      />
                    </div>
                  </div>
                );
              } else {
                return (
                  <div key={aspect.localizedAspectName} className={`flex items-center border-b border-gray-100 last:border-b-0 ${baseBg} transition-colors`}>
                    <div className="w-1/3 px-3 py-2 text-sm font-medium text-gray-700 border-r border-gray-300">
                      {toTitleCase(label)}
                    </div>
                    <div className="flex-1 px-3 py-2">
                      <ComboBoxInput
                        size="sm"
                        options={aspect.aspectValues}
                        searchField={"localizedValue"}
                        register={register}
                        setValue={setValue}
                        registerName={aspect.localizedAspectName}
                        initialValue={initialValue}
                      />
                    </div>
                  </div>
                );
              }
            })}
          </div>
        </div>

        <div className="flex justify-end gap-4 mt-6">
          {setPage && (
            <ButtonInput
              label={"Back"}
              isSubmit={false}
              onClick={() => setPage((p: number) => p - 1)}
              classes={"!bg-white !border-1 !border-gray-400 !text-gray-600 hover:!bg-gray-200"}
            />
          )}

          <ButtonInput
            label={"Save"}
            isSubmit={true}
            onClick={undefined}
            classes={undefined}
            isLoading={createUpdate.isLoading}
          />

          {setPage && (
            <ButtonInput
              label={"Publish"}
              isSubmit={false}
              onClick={() => onSubmit(getValues(), true)}
              classes={undefined}
              disabled={(part.offers[0]?.publishedOn !== null && part.offers[0]?.listingEnded === null)  || errorCheck?.isDisabled}
              isLoading={createUpdate.isLoading || publish.isLoading}

            />
          )}
        </div>

        {errorCheck && errorCheck.isDisabled && (
          <div className="text-right">
            <ul className="text-red-600 text-sm mt-2 list-disc list-inside space-y-0.5">
              {(errorCheck.errors?.length ? errorCheck.errors : [errorCheck.error]).map((err) => (
                <li key={err}>{err}</li>
              ))}
            </ul>
          </div>
        )}


      </form>

      {/* Add Custom Aspect Section */}
      <div className="flex flex-col gap-2 mt-4">
        <div className="border border-gray-200 rounded p-3">
          <h2 className="text-sm font-semibold mb-2">Add Custom Aspect:</h2>
          <div className="grid grid-cols-2 gap-3">

            <TextInput label={"Name: (Max: 40)"} register={register} registerName={"newAspectName"} maxLength={40} size="sm" />
            <TextInput label={"Value: (Max: 50)"} register={register} registerName={"newAspectValue"} maxLength={50} size="sm" />

          </div>
          <div className="mt-2">
            <ButtonInput
              label={"Add Aspect"}
              isSubmit={false}
              onClick={() => addCustomAspect(getValues().newAspectName, getValues().newAspectValue)}
              classes={undefined}
            />
          </div>
        </div>
      </div>
    </div>
  )
}